import React, { useState } from 'react';
import PropTypes from 'prop-types';

import ManagedFormGroup from 'components/form/managed_input/form_group';
import ManagedInput from 'components/form/managed_input';
import FormModal from 'components/form_modal';
import Options from 'components/form/managed_input/options';
import { Select } from 'components/ui/select';
import { defaultCategoryName, transactionSideOptions } from './utils';
import { saveDefaultDeduction } from './api';

const defaultForm = {
  category: defaultCategoryName,
  dealTypes: [],
  hasDealTypes: false,
  unit: 'unit_dollars',
};

export default function DefaultDeductionModal({
  categories,
  defaultDeduction,
  features,
  options,
  show,
  onHide,
  onSave,
}) {
  if (features.transactionSideDetails) {
    defaultForm.transactionSide = transactionSideOptions.find(option => option.default).value;
  }

  const [form, setForm] = useState(defaultForm);

  function reset() {
    if (defaultDeduction) {
      setForm({ ...defaultDeduction, hasDealTypes: defaultDeduction.dealTypes.length > 0 });
    } else {
      setForm(defaultForm);
    }
  }

  function changeForm(changes) {
    setForm({ ...form, ...changes });
  }

  function handleSuccess(defaultDeduction) {
    onSave(defaultDeduction);
    onHide();
  }

  return (
    <FormModal
      header={`${defaultDeduction ? 'Edit' : 'Create'} Default Deduction`}
      show={show}
      onCancel={onHide}
      onShow={reset}
      onSubmit={() => saveDefaultDeduction(form)}
      onSuccess={handleSuccess}
    >
      <ManagedFormGroup
        label="Category"
        name="category"
        type="select"
        value={form.category}
        onChange={changeForm}
      >
        <Options
          labelName="name"
          options={[...categories, { name: defaultCategoryName }]}
          valueName="name"
        />
      </ManagedFormGroup>

      {features.tieredDeductions &&
        <ManagedFormGroup
          label="Tier"
          name="tier"
          placeholder={categories.find(category => category.name === form.category)?.tier}
          type="number"
          value={form.tier}
          onChange={changeForm}
        />
      }

      {features.transactionSideDetails &&
        <ManagedFormGroup
          label="Transaction Side"
          name="transactionSide"
          type="select"
          value={form.transactionSide}
          onChange={changeForm}
        >
          <Options options={transactionSideOptions} />
        </ManagedFormGroup>
      }

      <div className="form-group">
        <label className="form-label">Amount</label>
        <div className="d-flex gap-2">
          <ManagedInput
            className="w-auto"
            name="unit"
            type="select"
            value={form.unit}
            onChange={changeForm}
          >
            <Options options={options.unit} />
          </ManagedInput>

          <ManagedInput
            className="form-control"
            name="value"
            type="number"
            value={form.value}
            onChange={changeForm}
          />
        </div>
      </div>

      <ManagedFormGroup
        label="Description"
        name="description"
        type="textarea"
        value={form.description}
        onChange={changeForm}
      />

      <div className="mt-3">
        <div>
          <label>
            <input
              checked={!form.hasDealTypes}
              type="radio"
              onChange={() => changeForm({ dealTypes: [], hasDealTypes: false })}
            /> All Deals
          </label>
        </div>

        <div className="row d-flex align-items-center">
          <div className="col-3 text-nowrap">
            <label>
              <input
                checked={form.hasDealTypes}
                type="radio"
                onChange={() => changeForm({ hasDealTypes: true })}
              /> Deal Type
            </label>
          </div>

          <div className="col-9">
            <Select
              isDisabled={!form.hasDealTypes}
              isMulti
              options={options.dealType}
              value={form.dealTypes.map(dealType => options.dealType.find(option => option.value === dealType))}
              onChange={options => changeForm({ dealTypes: options.map(option => option.value) })}
            />
          </div>
        </div>
      </div>
    </FormModal>
  );
}

DefaultDeductionModal.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    tier: PropTypes.number,
  })).isRequired,
  defaultDeduction: PropTypes.shape({
    dealTypes: PropTypes.array.isRequired,
  }),
  features: PropTypes.shape({
    tieredDeductions: PropTypes.bool,
    transactionSideDetails: PropTypes.bool,
  }).isRequired,
  options: PropTypes.shape({
    dealType: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
    })).isRequired,
    unit: PropTypes.array.isRequired,
  }).isRequired,
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};
